import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/* Where an item lives. The path names the page that works on it and, where the page lists rows,
 * `?focus=<id>` so the page scrolls to that row (and a leave/overtime request opens for review). */
export function itemPath(kind, it) {
    const id = it?.id;
    switch (kind) {
        case "leave": return `/timekeeping/leaves?focus=${id}`;
        case "overtime": return `/timekeeping/overtime?focus=${id}`;
        case "attendance": return `/timekeeping/attendance?focus=${id}`;
        case "payroll": return `/payroll/runs/${id}`;
        case "helpdesk": return `/helpdesk?focus=${id}`;
        case "candidate": return "/recruitment";
        case "onboarding": return "/onboarding";
        case "final_pay": return `/payroll/final-pay?focus=${id}`;
        case "document": return `/hris/document-requests?focus=${id}`;
        case "time_adjustment":
        case "schedule_adjustment": return `/timekeeping/adjustments?focus=${id}`;
        default: return "/approvals";
    }
}

// Links written into notifications before the routes were settled.
const LEGACY = { "/hris/offboarding": "/offboarding" };
export const fixLink = (link) => LEGACY[link] || link;

/* Open a path in the company it belongs to. Pages work on ONE company, so an item from another
 * company switches to it first (the same switch the company menu does), then opens the page. */
export function useOpenTarget() {
    const { activeCompanyId, switchCompany, companies } = useAuth();
    const navigate = useNavigate();
    return (path, companyId) => {
        const target = fixLink(path);
        const known = (companies || []).some((c) => c.id === companyId);
        if (companyId && known && companyId !== activeCompanyId) {
            switchCompany(companyId);
            window.location.assign(target);
        } else {
            navigate(target);
        }
    };
}

/* A page that was opened with ?focus=<id> scrolls to the row marked data-row-id=<id> and flashes it.
 * The row may not exist yet (the page is still loading), so watch for it for a few seconds. */
export function useFocusFromUrl() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        const id = new URLSearchParams(search).get("focus");
        if (!id) return undefined;
        const selector = `[data-row-id="${String(id).replace(/"/g, "")}"]`;
        const flash = () => {
            const el = document.querySelector(selector);
            if (!el) return false;
            el.scrollIntoView({ block: "center", behavior: "smooth" });
            el.classList.add("focus-flash");
            setTimeout(() => el.classList.remove("focus-flash"), 4500);
            return true;
        };
        if (flash()) return undefined;
        const mo = new MutationObserver(() => { if (flash()) mo.disconnect(); });
        mo.observe(document.body, { childList: true, subtree: true });
        const stop = setTimeout(() => mo.disconnect(), 10000);
        return () => { mo.disconnect(); clearTimeout(stop); };
    }, [pathname, search]);
}
